import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import { getIntl } from '../../utils/intl';
import urls from '../../utils/urls';
import { getMyOrganizationClientInvitationsCacheKey, onRespondToOrganizationClientInvitation } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useNotify from '../useNotify';
import { useExplicitAppType } from '../useAppType';

const intl = getIntl('useMyOrganizationClientInvitations', {
    errorLoadingClientInvitations: 'Failed to load client invitations',
    errorRespondingToInvitation: 'Failed to respond to client invitation',
    successAccept: 'Invitation accepted',
    successDecline: 'Invitation declined',
    downloadInvitationFileFailure: 'Failed to download invitation file',
});

export type RespondToInvitationArgs = {
    organizationId: string;
    invitationId: string;
    response: APITypes.organizationService.OrganizationClientInvitationResponseRequest;
};

export type DownloadInvitationFileArgs = {
    invitationId: string;
    organizationId: string;
    fileId: string;
};

export type OnRespondToInvitationArgs = {
    response: APITypes.organizationService.OrganizationClientInvitationResponseResponse;
    request: RespondToInvitationArgs;
};

export type UseMyOrganizationClientInvitationsArgs = {
    onReceivedFileDownloadUrl?: (url: string) => void;
    onRespondToInvitation?: (args: OnRespondToInvitationArgs) => void;
};

export type UseMyOrganizationClientInvitationsReturnValue = {
    clientInvitations: APITypes.organizationService.GetOrganizationClientInvitationMeResponse['items'];
    isLoadingClientInvitations: boolean;
    respondToInvitation: (args: RespondToInvitationArgs) => void;
    isRespondingToInvitation: boolean;
    downloadInvitationFile: (args: DownloadInvitationFileArgs) => void;
    isDownloadingInvitationFile: boolean;
};

type ElementType<T> = T extends (infer U)[] ? U : never;

export type ClientInvitation = ElementType<
    APITypes.organizationService.GetOrganizationClientInvitationMeResponse['items']
>;

export const useMyOrganizationClientInvitations = ({
    onReceivedFileDownloadUrl,
    onRespondToInvitation,
}: UseMyOrganizationClientInvitationsArgs = {}): UseMyOrganizationClientInvitationsReturnValue => {
    const appType = useExplicitAppType();
    const notify = useNotify();

    const myOrganizationClientInvitationsQuery = useQuery({
        queryKey: getMyOrganizationClientInvitationsCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationClientInvitationMeResponse>(
                urls.api.organizations.clientInvitations.myInvitations,
            );
        },
        enabled: appType !== 'professional',
    });
    useQueryErrorNotify(myOrganizationClientInvitationsQuery, intl('errorLoadingClientInvitations'));

    const respondToInvitationMutation = useMutation({
        mutationFn: async ({ organizationId, invitationId, response }: RespondToInvitationArgs) => {
            return appAPI.post<APITypes.organizationService.OrganizationClientInvitationResponseResponse>(
                urls.api.organizations.clientInvitations.organizationClientInvitationResponse(
                    organizationId,
                    invitationId,
                ),
                { body: response },
            );
        },
        onError: () => {
            notify('error', intl('errorRespondingToInvitation'));
        },
        onSuccess: (data, args) => {
            onRespondToOrganizationClientInvitation.resetCache({})();
            onRespondToInvitation?.({
                request: args,
                response: data,
            });
            if (args.response.response === 'accept') {
                notify('success', intl('successAccept'));
            } else {
                notify('success', intl('successDecline'));
            }
        },
    });

    const downloadInvitationFile = useMutation({
        mutationFn: async (args: DownloadInvitationFileArgs) => {
            const response =
                await appAPI.get<APITypes.organizationService.GetOrganizationClientInvitationFileDownloadUrlResponse>(
                    urls.api.organizations.clientInvitations.organizationClientInvitationFile(
                        args.organizationId,
                        args.invitationId,
                        args.fileId,
                    ),
                );
            return response.downloadUrl;
        },
        onSuccess: (data) => {
            onReceivedFileDownloadUrl?.(data);
        },
        onError: () => {
            notify('error', intl('downloadInvitationFileFailure'));
        },
    });

    return {
        clientInvitations: myOrganizationClientInvitationsQuery.data?.items || [],
        isLoadingClientInvitations: myOrganizationClientInvitationsQuery.isLoading,
        respondToInvitation: respondToInvitationMutation.mutate,
        isRespondingToInvitation: respondToInvitationMutation.isPending,
        downloadInvitationFile: downloadInvitationFile.mutate,
        isDownloadingInvitationFile: downloadInvitationFile.isPending,
    };
};
